/**
 * Global error handler utility
 * This can be integrated with your existing error handling system
 */

import * as Sentry from '@sentry/react';
import { AppError, OnboardingError, DataTransformationError, APIResponseError } from './errors';

/**
 * Global error handler function
 * @param {Error} error - The error to handle
 * @param {string} context - Context where the error occurred (e.g., component name)
 */
export const handleGlobalError = (error, context = 'Unknown') => {
  // Create error report object
  const errorReport = {
    timestamp: new Date().toISOString(),
    context,
    message: error.message,
    stack: error.stack,
    type: error.constructor.name,
    ...getErrorSpecificData(error)
  };

  // Handle different error types
  switch (true) {
    case error instanceof OnboardingError:
      handleOnboardingError(errorReport, error);
      break;

    case error instanceof DataTransformationError:
      handleDataTransformationError(errorReport, error);
      break;

    case error instanceof APIResponseError:
      handleAPIResponseError(errorReport, error);
      break;

    case error instanceof AppError:
      handleAppError(errorReport, error);
      break;

    default:
      handleUnknownError(errorReport, error);
      break;
  }
};

/**
 * Extract error-specific data based on error type
 */
const getErrorSpecificData = (error) => {
  const data = {};
  
  if (error.code) {
    data.code = error.code;
  }
  
  if (error.statusCode) {
    data.statusCode = error.statusCode;
  }
  
  if (error instanceof DataTransformationError && error.originalData) {
    data.originalData = error.originalData;
  }
  
  if (error instanceof APIResponseError && error.response) {
    data.response = error.response;
  }
  
  return data;
};

/**
 * Handle onboarding-specific errors
 */
const handleOnboardingError = (errorReport, error) => {
  // Log to monitoring service (e.g., Sentry, LogRocket, etc.)
  logToMonitoringService('onboarding_error', errorReport, error);
  
  // Show user-friendly message
  showUserNotification('There was an issue with the onboarding process. Please try again or contact support.');
  
  // Track analytics event
  trackAnalyticsEvent('onboarding_error', {
    error_code: errorReport.code,
    context: errorReport.context
  });
};

/**
 * Handle data transformation errors
 */
const handleDataTransformationError = (errorReport, error) => {
  // Log to monitoring service
  logToMonitoringService('data_transformation_error', errorReport, error);
  
  // Show user-friendly message
  showUserNotification('There was an issue processing your data. Please refresh and try again.');
  
  // Track analytics event
  trackAnalyticsEvent('data_transformation_error', {
    error_code: errorReport.code,
    context: errorReport.context
  });
};

/**
 * Handle API response errors
 */
const handleAPIResponseError = (errorReport, error) => {
  // Log to monitoring service
  logToMonitoringService('api_response_error', errorReport, error);
  
  // Show user-friendly message based on status code
  const message = errorReport.statusCode >= 500
    ? 'Our servers are experiencing issues. Please try again later.'
    : 'There was an issue with your request. Please check your input and try again.';
  
  showUserNotification(message);
  
  // Track analytics event
  trackAnalyticsEvent('api_response_error', {
    error_code: errorReport.code,
    status_code: errorReport.statusCode,
    context: errorReport.context
  });
};

/**
 * Handle generic app errors
 */
const handleAppError = (errorReport, error) => {
  // Log to monitoring service
  logToMonitoringService('app_error', errorReport, error);
  
  // Show generic user-friendly message
  showUserNotification('Something went wrong. Please try again or contact support if the issue persists.');
  
  // Track analytics event
  trackAnalyticsEvent('app_error', {
    error_code: errorReport.code,
    context: errorReport.context
  });
};

/**
 * Handle unknown/unexpected errors
 */
const handleUnknownError = (errorReport, error) => {
  // Log to monitoring service with high priority
  logToMonitoringService('unknown_error', errorReport, error, 'high');
  
  // Show generic message
  showUserNotification('An unexpected error occurred. Please refresh the page and try again.');
  
  // Track analytics event
  trackAnalyticsEvent('unknown_error', {
    context: errorReport.context
  });
};

/**
 * Log error to monitoring service (implement based on your monitoring solution)
 */
const logToMonitoringService = (errorType, errorReport, error, priority = 'normal') => {
  // Sentry
  Sentry.captureException(error, {
    tags: { error_type: errorType, priority, context: errorReport.context },
    extra: errorReport,
  });

  // LogRocket
  // LogRocket.captureException(new Error(errorReport.message));

  // Custom logging service
  // logService.error(errorType, errorReport);

  // Also log to console in development
  if (import.meta.env.VITE_NODE_ENV === 'development') {
    console.error(`[${errorType}]`, errorReport);
  }
};

/**
 * Show user-friendly notification (implement based on your notification system)
 */
const showUserNotification = (message) => {
  // Example implementations:
  
  // React Toast
  // toast.error(message);
  
  // Custom notification system
  // NotificationService.error(message);
  
  // For now, we'll use alert in development (replace with proper notification)
  if (process.env.NODE_ENV === 'development') {
    alert(`Error: ${message}`);
  }
};

/**
 * Track analytics event (implement based on your analytics solution)
 */
const trackAnalyticsEvent = (eventName, properties) => {
  // Example implementations:
  
  // Google Analytics
  // gtag('event', eventName, properties);
  
  // Mixpanel
  // mixpanel.track(eventName, properties);
  
  // Custom analytics
  // AnalyticsService.track(eventName, properties);
  
  // For now, we'll log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Analytics] ${eventName}`, properties);
  }
};

/**
 * React Error Boundary helper
 * Use this in your error boundaries to handle React component errors
 */
export const handleReactError = (error, errorInfo, componentName = 'Unknown') => {
  const enhancedError = new AppError(
    `React component error in ${componentName}: ${error.message}`,
    'REACT_COMPONENT_ERROR',
    500
  );
  
  // Add React-specific information
  enhancedError.componentStack = errorInfo.componentStack;
  enhancedError.originalError = error;
  
  handleGlobalError(enhancedError, `React:${componentName}`);
};
